/**
 * One lesson, as it stands for one student. Opened from a row on the syllabus
 * and from a block on the board, so both answer the same question: what is this
 * lesson, what does it take to pass, and where does it stand.
 */
import { useQuery } from '../api.js';
import { Tile } from '../controls.js';
import { Drawer, Pane, Panes, Pill, Spinner } from '../ui.js';
import { AlertIcon, CheckIcon, ClockIcon, LockIcon, PlayIcon } from '../icons.js';

interface LessonDetail {
  lesson: {
    id: number; code: string; name: string; stage: number; seq: number;
    objective: string; completionStandard: string;
    flightHours: number; groundHours: number;
    isStageCheck: boolean; isSolo: boolean; isNight: boolean; isXc: boolean;
    requiresIfr: boolean; isGround: boolean; isRemedial: boolean;
  };
  course: { id: number; name: string; regulation: string };
  record: {
    flownOn: string | null; flightHours: number; groundHours: number;
    nightHours: number; instrumentHours: number; soloHours: number;
    grade: 'S' | 'U' | 'I' | null; notes: string;
    approvedOn: string | null; approvedByName: string | null;
  } | null;
  gate: { title: string; body: string } | null;
  scheduled: {
    id: number; startsAt: number; endsAt: number; status: string;
    aircraftTail: string; aircraftModel: string; instructorName: string;
    hobbsStart: number | null; hobbsEnd: number | null;
  }[];
}

const GRADE_LABEL: Record<string, string> = {
  S: 'S · satisfactory', U: 'U · unsatisfactory, repeat', I: 'I · incomplete',
};

export function LessonSheet({ studentId, lessonId, onClose }: {
  studentId: number;
  lessonId: number;
  onClose: () => void;
}): JSX.Element {
  const { data, error } = useQuery<LessonDetail>(`/students/${studentId}/lessons/${lessonId}`);

  if (error) return <Drawer title="Lesson" onClose={onClose}><div className="empty">{error}</div></Drawer>;
  if (!data) return <Drawer title="Lesson" onClose={onClose}><Spinner /></Drawer>;

  const l = data.lesson;
  const complete = data.record?.grade === 'S';
  const flags: [string, boolean][] = [
    ['Stage check', l.isStageCheck],
    ['Remedial', l.isRemedial],
    ['Solo', l.isSolo],
    ['Night', l.isNight],
    ['Cross-country', l.isXc],
    ['Instrument', l.requiresIfr],
    ['Ground only', l.isGround],
  ];

  return (
    <Drawer
      title={`${l.code} · ${l.name}`}
      subtitle={`${data.course.name} · Part ${data.course.regulation} · Stage ${l.stage}`}
      onClose={onClose}
    >
      <Panes>
        <Pane title="The lesson">
          <div className="row" style={{ gap: 11, flexWrap: 'nowrap', alignItems: 'flex-start', marginBottom: 12 }}>
            <Tile color={complete ? 'green' : l.isRemedial ? 'orange' : data.gate ? 'gray' : 'blue'}>
              {complete ? <CheckIcon /> : l.isRemedial ? <AlertIcon /> : data.gate ? <LockIcon /> : <PlayIcon />}
            </Tile>
            <span className="row" style={{ gap: 6 }}>
              {flags.filter(([, on]) => on).map(([label]) => <Pill key={label} tone="blue">{label}</Pill>)}
              {!flags.some(([, on]) => on) && <Pill tone="gray">Dual</Pill>}
            </span>
          </div>

          <h4 className="closeout__h">Objective</h4>
          <p style={{ margin: '6px 0 14px', fontSize: 13.5, lineHeight: 1.55, textWrap: 'pretty', maxWidth: '72ch' }}>
            {l.objective || <span className="muted">Not recorded.</span>}
          </p>

          <h4 className="closeout__h">Completion standard</h4>
          <p style={{ margin: '6px 0 14px', fontSize: 13.5, lineHeight: 1.55, textWrap: 'pretty', maxWidth: '72ch' }}>
            {l.completionStandard || <span className="muted">Not recorded.</span>}
          </p>

          <h4 className="closeout__h">Planned</h4>
          <div className="stack" style={{ gap: 4, marginTop: 6, fontSize: 13 }}>
            {!l.isGround && (
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="muted">Flight</span><strong>{l.flightHours.toFixed(1)} hrs</strong>
              </div>
            )}
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Ground</span><strong>{l.groundHours.toFixed(1)} hrs</strong>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Position</span><strong>Lesson {l.seq} of the course</strong>
            </div>
          </div>

          {data.gate && (
            <div style={{
              marginTop: 14, padding: '10px 12px', borderRadius: 'var(--r-md)',
              background: 'var(--tint-red)',
              color: 'color-mix(in srgb, var(--sys-red) 88%, var(--label-1))',
              fontSize: 12.5, textWrap: 'pretty',
            }}>
              <strong style={{ display: 'block' }}>{data.gate.title}</strong>
              {data.gate.body}
            </div>
          )}
        </Pane>

        <Pane title="The record" flush>
          {!data.record ? (
            <div className="empty" style={{ padding: 24 }}>
              {l.isRemedial
                ? 'Flown only if a lesson in this stage is unsatisfactory.'
                : 'Not flown yet.'}
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 24px 8px' }}>
                <Pill>{data.record.grade ? GRADE_LABEL[data.record.grade] : 'Not graded'}</Pill>
              </div>
              <div className="kv"><span>Flown</span><span>{data.record.flownOn ?? '—'}</span></div>
              <div className="kv"><span>Flight</span><span>{data.record.flightHours.toFixed(1)} hrs</span></div>
              <div className="kv"><span>Ground</span><span>{data.record.groundHours.toFixed(1)} hrs</span></div>
              {data.record.nightHours > 0 && (
                <div className="kv"><span>Night</span><span>{data.record.nightHours.toFixed(1)} hrs</span></div>
              )}
              {data.record.instrumentHours > 0 && (
                <div className="kv"><span>Instrument</span><span>{data.record.instrumentHours.toFixed(1)} hrs</span></div>
              )}
              {data.record.soloHours > 0 && (
                <div className="kv"><span>Solo</span><span>{data.record.soloHours.toFixed(1)} hrs</span></div>
              )}
              {data.record.approvedOn && (
                <div className="kv">
                  <span>Approved</span>
                  <span>{data.record.approvedByName} · {data.record.approvedOn}</span>
                </div>
              )}
              {data.record.notes && (
                <div style={{ padding: '12px 24px 0' }}>
                  <h4 className="closeout__h">Instructor notes</h4>
                  <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, textWrap: 'pretty' }}>
                    {data.record.notes}
                  </p>
                </div>
              )}
            </>
          )}
        </Pane>

        <Pane title="On the schedule" flush>
          {data.scheduled.length === 0 && (
            <div className="empty" style={{ padding: 24 }}>Not booked.</div>
          )}
          {data.scheduled.map((r) => (
            <div key={r.id} style={{ padding: '12px 24px', borderBottom: '0.5px solid var(--sep-soft)' }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
                <strong style={{ fontSize: 13.5 }}>
                  {new Date(r.startsAt).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                  {' '}{time(r.startsAt)}–{time(r.endsAt)}
                </strong>
                <Pill>{r.status}</Pill>
              </div>
              <div className="small muted" style={{ marginTop: 3 }}>
                {r.aircraftTail}{r.aircraftModel && ` · ${r.aircraftModel}`}
                {r.instructorName && ` · ${r.instructorName}`}
              </div>
              {r.hobbsStart != null && r.hobbsEnd != null && (
                <div className="small muted" style={{ marginTop: 2 }}>
                  Hobbs {r.hobbsStart.toFixed(1)} → {r.hobbsEnd.toFixed(1)}
                  {' · '}{(r.hobbsEnd - r.hobbsStart).toFixed(1)} hrs
                </div>
              )}
            </div>
          ))}
          <div style={{ padding: '12px 24px' }} className="small muted">
            <ClockIcon style={{ width: 13, height: 13, verticalAlign: '-2px', marginRight: 5 }} />
            Every time this lesson has been on the board, past and future.
          </div>
        </Pane>
      </Panes>
    </Drawer>
  );
}

function time(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}
